import * as i0 from '@angular/core';
import { inject, Injectable, InjectionToken, PLATFORM_ID, importProvidersFrom, NgModule, makeEnvironmentProviders } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { BASE_URL } from '@teamfoster/sdk/ssr';
import { createAction, props, createReducer, on, createFeatureSelector, createSelector, Store, StoreModule, provideState } from '@ngrx/store';
import { take, switchMap, map, catchError, tap, filter } from 'rxjs/operators';
import { createEffect, ofType, Actions, EffectsModule, provideEffects } from '@ngrx/effects';
import { of } from 'rxjs';
import { isPlatformBrowser } from '@angular/common';
import { RouterModule } from '@angular/router';
let LanguageService = /*#__PURE__*/(() => {
  class LanguageService {
    constructor() {
      this.http = inject(HttpClient);
      this.BASE_URL = inject(BASE_URL, {
        optional: true
      }) || '/';
    }
    getLanguages() {
      return this.http.get(`${this.BASE_URL}api/languages`);
    }
    static {
      this.ɵfac = function LanguageService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LanguageService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: LanguageService,
        factory: LanguageService.ɵfac
      });
    }
  }
  return LanguageService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * This is not a real service, but it looks like it from the outside.
 * It's just an InjectionTToken used to import the config object, provided from the outside
 */
const LanguageConfigService = new InjectionToken('LanguageConfig');
const LoadLanguages = createAction('[Language] Load Languages');
// export const LoadLanguagesSuccess = createAction('[Language] Load Language Success', props<{ languages: Language[] }>());
const LoadLanguagesSuccess = createAction('[Language] Load Language Success', props());
const LoadLanguagesFail = createAction('[Language] Load Languages Fail', props());
const SetActiveLanguage = createAction('[Language] Set Active Language', props());
const SetActiveLanguageSuccess = createAction('[Language] Set Active Language Success', props());
const InitActiveLanguage = createAction('[Language] Initialize selected language');
const InitActiveLanguageComplete = createAction('[Language] Initialize selected language Complete', props());
function ToEntities(a, key, initialEntities) {
  return a.reduce((e, obj) => {
    return {
      ...e,
      [obj[key]]: obj
    };
  }, {
    ...initialEntities
  });
}
const initialState = {
  entities: {},
  active: '',
  initialized: false,
  initializing: false,
  loaded: false,
  loading: false
};
const LanguageReducer = createReducer(initialState, on(LoadLanguagesSuccess, (state, {
  languages
}) => {
  const entities = ToEntities(languages, 'id', state.entities);
  return {
    ...state,
    entities,
    loading: false,
    loaded: true
  };
}), on(LoadLanguagesFail, (state, {
  error
}) => {
  return {
    ...state,
    loading: false,
    loaded: false,
    error
  };
}), on(InitActiveLanguage, state => {
  return {
    ...state,
    initializing: true
  };
}), on(InitActiveLanguageComplete, (state, {
  activeLang
}) => {
  return {
    ...state,
    active: activeLang,
    initializing: false,
    initialized: true
  };
}), on(SetActiveLanguageSuccess, (state, {
  activeLang
}) => {
  return {
    ...state,
    active: activeLang
  };
}));
const getLanguagesEntities$1 = state => state.entities;
const getActiveLanguage$1 = state => state.active;
const getLanguagesLoading = state => state.loading;
const getLanguagesLoaded = state => state.loaded;
const getLanguagesInitializing$1 = state => state.initializing;
const getLanguagesInitialized$1 = state => state.initialized;
const featureKey = 'languages';
const reducers = {
  languages: LanguageReducer
};
const getLanguageState = createFeatureSelector(featureKey);
const getLanguageSelectorState = createSelector(getLanguageState, state => state.languages);
const getLanguagesEntities = createSelector(getLanguageSelectorState, getLanguagesEntities$1);
const getActiveLanguage = createSelector(getLanguageSelectorState, getActiveLanguage$1);
const getLanguesLoaded = createSelector(getLanguageSelectorState, getLanguagesLoaded);
const getLanguesLoading = createSelector(getLanguageSelectorState, getLanguagesLoading);
const getLanguagesInitialized = createSelector(getLanguageSelectorState, getLanguagesInitialized$1);
const getLanguagesInitializing = createSelector(getLanguageSelectorState, getLanguagesInitializing$1);
const getAllLanguages = createSelector(getLanguagesEntities, entities => {
  return Object.keys(entities).map(id => entities[id]);
});
const getOrderedLanguages = createSelector(getAllLanguages, languages => {
  return languages.sort((a, b) => a.name.localeCompare(b.name));
});
const getSwitchableLanguages = createSelector(getOrderedLanguages, languages => {
  return languages.filter(a => a.inSwitcher);
});
const getActiveLanguageNoCulture = createSelector(getActiveLanguage, lang => {
  return lang?.split('-')[0]?.toUpperCase();
});
const LanguageInterceptor = (req, next) => {
  const store = inject(Store);
  const baseUrl = inject(BASE_URL);
  if (!req.url.startsWith(baseUrl)) {
    return next(req);
  }
  return store.select(getActiveLanguage).pipe(take(1), switchMap(lang => next(req.clone({
    headers: req.headers.set('Language', `${lang}`)
  }))));
};
const LoadLanguages$ = createEffect((actions$ = inject(Actions), langService = inject(LanguageService)) => {
  return actions$.pipe(ofType(LoadLanguages), switchMap(() => langService.getLanguages().pipe(map(languages => LoadLanguagesSuccess({
    languages
  })), catchError(error => of(LoadLanguagesFail({
    error
  }))))));
}, {
  functional: true
});
const setActiveLanguage$ = createEffect((actions$ = inject(Actions), platformId = inject(PLATFORM_ID), config = inject(LanguageConfigService)) => {
  return actions$.pipe(ofType(SetActiveLanguage), map(({
    activeLang,
    preventRedirect
  }) => {
    if (isPlatformBrowser(platformId)) {
      localStorage.setItem(config.localStorageKey, activeLang);
      if (!preventRedirect) {
        location.reload();
      }
    }
    // this.lng.setLanguage(activeLang);
    return SetActiveLanguageSuccess({
      activeLang
    });
  }));
}, {
  functional: true
});
const InitLanguageSettings$ = createEffect((actions$ = inject(Actions), platformId = inject(PLATFORM_ID), config = inject(LanguageConfigService), baseUrl = inject(BASE_URL, {
  optional: true
})) => {
  return actions$.pipe(ofType(InitActiveLanguage), map(() => {
    if (!isPlatformBrowser(platformId)) {
      if (config.mapping && baseUrl) {
        const domain = config.mapping.find(a => baseUrl?.indexOf(a.domainPart) >= 0);
        if (domain) {
          return InitActiveLanguageComplete({
            activeLang: domain.language
          });
        }
      }
      return InitActiveLanguageComplete({
        activeLang: config.defaultLanguage
      });
    }
    const storedLanguageSettings = localStorage.getItem(config.localStorageKey);
    if (storedLanguageSettings) {
      return InitActiveLanguageComplete({
        activeLang: storedLanguageSettings
      });
    } else if (config.mapping && baseUrl) {
      const domain = config.mapping.find(a => baseUrl?.indexOf(a.domainPart) >= 0);
      if (domain) {
        return InitActiveLanguageComplete({
          activeLang: domain.language
        });
      }
    }
    return InitActiveLanguageComplete({
      activeLang: config.defaultLanguage
    });
  }));
}, {
  functional: true
});
var LanguageEffects = /*#__PURE__*/Object.freeze({
  __proto__: null,
  InitLanguageSettings$: InitLanguageSettings$,
  LoadLanguages$: LoadLanguages$,
  setActiveLanguage$: setActiveLanguage$
});
const effects = {
  ...LanguageEffects
};
const LanguageGuard = () => {
  const store = inject(Store);
  return store.select(getLanguesLoaded).pipe(tap(loaded => {
    if (!loaded) {
      store.dispatch(LoadLanguages());
    }
  }), filter(loaded => loaded), take(1)).pipe(switchMap(() => of(true)), catchError(() => of(false)));
};
const ActiveLanguageGuard = () => {
  const store = inject(Store);
  const platformId = inject(PLATFORM_ID);
  return store.select(getLanguagesInitialized).pipe(tap(loaded => {
    if (!loaded) {
      store.dispatch(InitActiveLanguage());
    }
  }), filter(loaded => loaded), map(loaded => loaded), take(1)).pipe(switchMap(() => of(true)), catchError(() => of(false)));
};
const storeFeature = StoreModule.forFeature(featureKey, reducers);
const effectsFeature = EffectsModule.forFeature(effects);
let LanguageNgrxModule = /*#__PURE__*/(() => {
  class LanguageNgrxModule {
    static forRoot(config) {
      config = {
        defaultLanguage: 'nl-NL',
        localStorageKey: 'LANGUAGE',
        mapping: [{
          domainPart: '.nl/',
          language: 'nl-NL'
        }],
        ...config
      };
      return {
        ngModule: LanguageNgrxModule,
        providers: [importProvidersFrom(storeFeature, effectsFeature), LanguageService, {
          provide: LanguageConfigService,
          useValue: config
        }]
      };
    }
    static {
      this.ɵfac = function LanguageNgrxModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || LanguageNgrxModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: LanguageNgrxModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({
        imports: [RouterModule]
      });
    }
  }
  return LanguageNgrxModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
function ProvideLanguageNgrx(config) {
  // using default values for missing properties
  const merged = {
    defaultLanguage: 'nl-NL',
    localStorageKey: 'LANGUAGE',
    mapping: [{
      domainPart: '.nl/',
      language: 'nl-NL'
    }],
    ...config
  };
  return makeEnvironmentProviders([provideState(featureKey, reducers), provideEffects(effects), LanguageService, {
    provide: LanguageConfigService,
    useValue: merged
  }]);
}

/**
 * Generated bundle index. Do not edit.
 */

export { ActiveLanguageGuard, InitActiveLanguage, InitActiveLanguageComplete, LanguageConfigService, LanguageGuard, LanguageInterceptor, LanguageNgrxModule, LanguageService, LoadLanguages, LoadLanguagesFail, LoadLanguagesSuccess, ProvideLanguageNgrx, SetActiveLanguage, SetActiveLanguageSuccess, ToEntities, effects, featureKey, getActiveLanguage, getActiveLanguageNoCulture, getAllLanguages, getLanguageSelectorState, getLanguageState, getLanguagesEntities, getLanguagesInitialized, getLanguagesInitializing, getLanguesLoaded, getLanguesLoading, getOrderedLanguages, getSwitchableLanguages, reducers };
